import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaEdit, FaTrash, FaHome, FaSearch, FaEllipsisV } from 'react-icons/fa';
import styled from 'styled-components';

const CardStyles = styled.div`
  width: 100%;
  
  @media (max-width: 768px) {
    .property-card {
      display: flex;
      flex-direction: column;
      margin-bottom: 1rem;
      padding: 1rem;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    
    .table-view {
      display: none;
    }
    
    .card-view {
      display: block;
    }
  }
  
  @media (min-width: 769px) {
    .card-view {
      display: none;
    }
    
    .table-view {
      display: table;
    }
  }
  
  .table-responsive {
    width: 100% !important;
    max-width: 100% !important;
  }
  
  .table {
    width: 100% !important;
    table-layout: fixed;
  }
  
  .card-body {
    padding: 0 !important;
    width: 100%;
  }
  
  .card-header {
    width: 100% !important;
    padding: 1rem !important;
    box-sizing: border-box !important;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100% !important;
  box-sizing: border-box !important;
  
  .heading-container {
    flex: 1;
    text-align: left;
    margin-right: 20px;
  }
  
  .search-container {
    flex: 0 0 auto;
    transition: all 0.3s ease;
  }
  
  .input-group {
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    position: relative; /* Add this */
  }
  
  .input-group-text, 
  .form-control {
    border: none;
  }
  
  .form-control {
    padding-left: 2.5rem; /* Add this to move placeholder text right */
  }
  
  .input-group-text {
    position: absolute; /* Change from relative to absolute */
    left: 0;
    top: 0;
    height: 100%;
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px; /* Set a fixed width */
  }
  
  .form-control:focus {
    box-shadow: none;
  }
  
  @media (max-width: 576px) {
    flex-direction: column;
    align-items: stretch;
    
    .heading-container {
      margin-bottom: 0.5em; /* Reduced from 0.75rem to 0.25rem */
      text-align: center;
      margin-right: 0;
    }
    
    .search-container {
      width: 100%;
      margin-top: 0; /* Ensure no extra margin */
    }
    
    .input-group {
      width: 100%;
      margin: 0 auto; /* Center the search bar */
    }
    
    .form-control {
      width: 100% !important; /* Override inline style */
      padding-left: 2.75rem; /* Increased padding for mobile */
    }
    
    .input-group-text {
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      z-index: 5;
      background: transparent !important;
      border: none;
      padding-left: 15px;
      display: flex;
      align-items: center;
    }
  }
`;

const PropertyCardHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
  
  img, .image-placeholder {
    width: 60px;
    height: 60px;
    border-radius: 8px;
    margin-right: 1rem;
    object-fit: cover;
  }
  
  .image-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f1f5f9;
  }
  
  .property-title {
    font-weight: 600;
  }
  
  .property-id {
    font-size: 0.75rem;
    color: #6c757d;
  }
`;

const PropertyCardContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
  
  .property-detail {
    flex: 1 0 50%;
    margin-bottom: 0.5rem;
    
    .label {
      font-size: 0.75rem;
      color: #6c757d;
    }
    
    .value {
      font-weight: 500;
    }
  }
`;

const PropertyList = ({ 
  loading, 
  properties, 
  propertyIds, 
  searchTerm, 
  setSearchTerm, 
  handleEditPropertyStart, 
  handleDeleteProperty 
}) => {
  const [viewMode, setViewMode] = useState('table');
  
  // Filter properties based on search term
  const filteredProperties = propertyIds?.filter(propertyId => {
    const property = properties?.[propertyId];
    return property?.propertyTitle?.toLowerCase().includes(searchTerm.toLowerCase()) || 
           property?.location?.toLowerCase().includes(searchTerm.toLowerCase());
  }) || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="card shadow-sm border-0 rounded-3 overflow-hidden w-100"
      style={{ width: '100%' }}
    >
      <div className="card-header bg-dark text-white p-3 w-100" style={{ width: '100%' }}>
        <SearchContainer>
          <div className="heading-container">
            <h3 className="mb-0" style={{ fontWeight: 'bold', letterSpacing: '0.5px' }}>Manage Properties</h3>
          </div>
          <div className="search-container">
            <div className="input-group">
              <span className="input-group-text bg-transparent border-end-0 text-light">
                <FaSearch />
              </span>
              <input
                type="text"
                className="form-control form-control-sm border-start-0 bg-dark text-light"
                placeholder="Search properties..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ transition: 'width 0.3s ease' }}
              />
            </div>
          </div>
        </SearchContainer>  
      </div>
      
      <CardStyles>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center my-5 py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">Loading properties...</p>
            </div>
          ) : (
            filteredProperties.length > 0 ? (
              <>
                {/* Table view (for desktop) */}
                <div className="table-responsive table-view w-100" style={{ width: '100%', maxWidth: '100%' }}>
                  <table className="table table-hover mb-0 w-100" style={{ width: '100%' }}>
                    <thead className="bg-light">
                      <tr>
                        <th style={{ width: '35%' }}>Property</th>
                        <th style={{ width: '20%' }}>Location</th>
                        <th style={{ width: '15%' }}>Price</th>
                        <th style={{ width: '15%' }}>Details</th>
                        <th style={{ width: '15%' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProperties.map((propertyId) => (
                        <motion.tr 
                          key={propertyId}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <td>
                            <div className="d-flex align-items-center gap-3">
                              {properties[propertyId].coverImageUrl ? (
                                <img 
                                  src={properties[propertyId].coverImageUrl} 
                                  alt={properties[propertyId].propertyTitle} 
                                  className="rounded"
                                  style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                />
                              ) : (
                                <div className="bg-light rounded text-center" style={{ width: '50px', height: '50px', lineHeight: '50px' }}>
                                  <FaHome />
                                </div>
                              )}
                              <div>
                                <h6 className="mb-0">{properties[propertyId].propertyTitle}</h6>
                                <small className="text-muted d-block">{propertyId}</small>
                              </div>
                            </div>
                          </td>
                          <td>{properties[propertyId].location}</td>
                          <td>
                            <span className="fw-bold">₹{properties[propertyId].startPrice?.toLocaleString()}</span>
                          </td>
                          <td>
                            <span className="badge bg-info text-dark me-1">{properties[propertyId].bhkCount} BHK</span>
                            <span className="badge bg-secondary">{properties[propertyId].maxOccupancy} guests</span>
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <motion.button 
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleEditPropertyStart(propertyId)}
                                title="Edit property"
                              >
                                <FaEdit />
                              </motion.button>
                              <motion.button 
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteProperty(propertyId)}
                                title="Delete property"
                              >
                                <FaTrash />
                              </motion.button>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Card view (for mobile) */}
                <div className="card-view">
                  {filteredProperties.map((propertyId) => (
                    <motion.div 
                      key={propertyId}
                      className="property-card bg-white"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      <PropertyCardHeader>
                        {properties[propertyId].coverImageUrl ? (
                          <img 
                            src={properties[propertyId].coverImageUrl} 
                            alt={properties[propertyId].propertyTitle}
                          />
                        ) : (
                          <div className="image-placeholder">
                            <FaHome />
                          </div>
                        )}
                        <div>
                          <div className="property-title">{properties[propertyId].propertyTitle}</div>
                          <div className="property-id">{propertyId}</div>
                        </div>
                      </PropertyCardHeader>
                      
                      <PropertyCardContent>
                        <div className="property-detail">
                          <div className="label">Location</div>
                          <div className="value">{properties[propertyId].location}</div>
                        </div>
                        <div className="property-detail">
                          <div className="label">Price</div>
                          <div className="value">₹{properties[propertyId].startPrice?.toLocaleString()}</div>
                        </div>
                        <div className="property-detail">
                          <div className="label">Configuration</div>
                          <div className="value">{properties[propertyId].bhkCount} BHK</div>
                        </div>
                        <div className="property-detail">
                          <div className="label">Max Guests</div>
                          <div className="value">{properties[propertyId].maxOccupancy} guests</div>
                        </div>
                      </PropertyCardContent>
                      
                      <div className="d-flex justify-content-end gap-2">
                        <motion.button 
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="btn btn-outline-primary"
                          onClick={() => handleEditPropertyStart(propertyId)}
                        >
                          <FaEdit className="me-1" /> Edit
                        </motion.button>
                        <motion.button 
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="btn btn-outline-danger"
                          onClick={() => handleDeleteProperty(propertyId)}
                        >
                          <FaTrash className="me-1" /> Delete
                        </motion.button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </>
            ) : (
              <div className="alert alert-info m-4">
                {searchTerm ? 'No properties match your search term.' : 'No properties found. Add a property to get started.'}
              </div>
            )
          )}
        </div>
      </CardStyles>
    </motion.div>
  );
};

export default PropertyList;
